/**
 * Filter schema for the engagement_tracker resource (frontend mirror of the
 * backend filterSchemas/engagement_tracker.js).
 *
 * Built dynamically from the grid response so dropdown options can pull
 * flow_states / flows / teams from the same payload the grid already loads.
 */

const NATIVE_FIELDS = [
	{ key: "name", label: "Engagement Name", type: "text", group: "Engagement Fields" },
	{ key: "notes", label: "Engagement Notes", type: "text", group: "Engagement Fields" },
	{
		key: "flow_id",
		label: "Workflow Type",
		type: "picklist",
		group: "Engagement Fields",
		options: ctx => (ctx.flows || []).map(f => ({ id: f.id, label: f.name })),
	},
	{
		key: "flow_state_id",
		label: "Status",
		type: "picklist",
		group: "Engagement Fields",
		// Group by workflow name — multiple workflows can have a status with the
		// same name (e.g. each of T4/T1/S1 has its own "Not Started"). Without
		// the group, the user can't tell them apart in the dropdown and ends up
		// filtering by the wrong flow_state_id.
		options: ctx => {
			const flowsById = {};
			(ctx.flows || []).forEach(f => {
				flowsById[f.id] = f;
			});
			return (ctx.flowStates || []).map(s => {
				const f = flowsById[s.flow_id];
				return {
					id: s.id,
					label: s.name,
					group: f ? f.name : null,
				};
			});
		},
	},
	{
		key: "team_id",
		label: "Team",
		type: "picklist",
		group: "Engagement Fields",
		options: ctx => (ctx.teams || []).map(t => ({ id: t.id, label: t.name })),
	},
	{ key: "tax_estimate", label: "Tax Estimate", type: "boolean", group: "Engagement Fields" },
	{ key: "due_date", label: "Due Date", type: "date", group: "Engagement Fields" },
	{ key: "date_in", label: "Date In", type: "date", group: "Engagement Fields" },
	{ key: "fiscal_year_end", label: "Year End", type: "date", group: "Engagement Fields" },
];

// Relational filters — match engagements that HAVE a task / member matching.
// These mirror the columns on the Task and User sub-grid forms.
const TASK_STATUS_OPTIONS = [
	{ id: "pending", label: "Pending" },
	{ id: "ready", label: "Ready" },
	{ id: "in_progress", label: "In Progress" },
	{ id: "done", label: "Done" },
];

const TASK_FIELDS = [
	{ key: "task:name", label: "Task Name", type: "text", group: "Task Fields" },
	{ key: "task:status", label: "Task Status", type: "picklist", group: "Task Fields", options: () => TASK_STATUS_OPTIONS },
	{ key: "task:deadline", label: "Task Deadline", type: "date", group: "Task Fields" },
	{ key: "task:client_id", label: "Task Client", type: "text", group: "Task Fields" },
	{ key: "task:notify", label: "Task Notify", type: "boolean", group: "Task Fields" },
	{ key: "task:description", label: "Task Description", type: "text", group: "Task Fields" },
	{ key: "task:notes", label: "Task Notes", type: "text", group: "Task Fields" },
];

const MEMBER_FIELDS = [
	{ key: "member:first_name", label: "Member First Name", type: "text", group: "Member Fields" },
	{ key: "member:last_name", label: "Member Last Name", type: "text", group: "Member Fields" },
	{
		key: "member:role_id",
		label: "Engagement Role",
		type: "picklist",
		group: "Member Fields",
		options: ctx => (ctx.roles || []).map(r => ({ id: r.id, label: r.name })),
	},
];

/**
 * Map a custom tracker column (from any resource) to a filter field. Tracker
 * dropdowns become "picklist" over the user-defined option keys; checkbox ->
 * boolean; everything else (text, date) -> text. The key prefix routes the
 * filter to the right backend source.
 */
function _customField(tf, { keyPrefix, group }) {
	const key = keyPrefix + tf.id;
	const label = tf.field_name + " (Custom)";
	// Derived columns (value mirrors a native engagement field) filter against the
	// real native column server-side, so offer that native type's operators —
	// including proper date operators, unlike free-text date customs.
	if (tf.derived_from && tf.derived_from.type) {
		const dt = tf.derived_from.type;
		return { key, label, type: dt === "checkbox" ? "boolean" : dt === "date" ? "date" : "text", group };
	}
	if (tf.field_type === "checkbox") {
		return { key, label, type: "boolean", group };
	}
	if (tf.field_type === "dropdown") {
		// Manual dropdown — user-defined option keys.
		if (Array.isArray(tf.options) && tf.options.length) {
			return {
				key,
				label,
				type: "picklist",
				group,
				options: () => tf.options.map(o => ({ id: o.key, label: o.label || o.key })),
			};
		}
		// Datasource-backed dropdown — options resolved server-side and passed in
		// via ctx.fieldOptions (keyed by field id). Stored value is the entity id
		// (a string), so option ids are stringified to match the text comparison.
		if (tf.datasource && tf.datasource.entity) {
			return {
				key,
				label,
				type: "picklist",
				group,
				options: ctx =>
					((ctx.fieldOptions && ctx.fieldOptions[tf.id]) || []).map(o => ({
						id: String(o.id),
						label: o.label,
					})),
			};
		}
	}
	return { key, label, type: "text", group };
}

/**
 * Build the runtime schema by combining native fields with this user's custom
 * tracker columns for engagements, tasks and members. Tracker dropdowns become
 * "picklist" (their values are stored as the option *key* string in the value
 * table); the UI uses the option list as the picker source.
 *
 *   trackerFields -> engagement custom columns  ("My Columns")
 *   taskFields    -> task custom columns        ("Task Fields")
 *   memberFields  -> user custom columns        ("Member Fields")
 */
export function buildEngagementTrackerSchema({ trackerFields = [], taskFields = [], memberFields = [] } = {}) {
	const fields = {};
	for (const f of NATIVE_FIELDS) fields[f.key] = f;
	for (const f of TASK_FIELDS) fields[f.key] = f;
	for (const f of MEMBER_FIELDS) fields[f.key] = f;

	for (const tf of trackerFields) {
		const f = _customField(tf, { keyPrefix: "tracker:", group: "My Columns" });
		fields[f.key] = f;
	}
	for (const tf of taskFields) {
		const f = _customField(tf, { keyPrefix: "task:custom:", group: "Task Fields" });
		fields[f.key] = f;
	}
	for (const tf of memberFields) {
		const f = _customField(tf, { keyPrefix: "member:custom:", group: "Member Fields" });
		fields[f.key] = f;
	}

	return { resource: "engagement_tracker", fields };
}
